import axios from "axios";
import { API_URL, currency, DALAPAY_PROVIDER_ID, DALAPAY_COUNTRY } from "./constants";
import {
  deleteTokenFromLocalStorage,
  getRefreshTokenFromLocalStorage,
  getTokenExpiryFromLocalStorage,
  getTokenFromLocalStorage,
  hydrateSessionTokenFromPersistentStorage,
  storeAuthSession,
} from "./utils";

const REFRESH_EARLY_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 5000;
const DEFAULT_REFRESH_DELAY_MS = 30 * 60 * 1000;
const CONFIGURED_REFRESH_DELAY_MS = Number(
  process.env.REACT_APP_FORCE_REFRESH_DELAY_MS || DEFAULT_REFRESH_DELAY_MS,
);

let refreshRequestPromise = null;
let scheduledRefreshId = null;

export const axiosApi = axios.create({
  baseURL: API_URL,
  Authorization: `Bearer ${getTokenFromLocalStorage()}`,
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    "Access-Control-Allow-Origin": "*",
    common: {
      Accept: "application/json",
    },
    get: {
      "Content-Type": "application/json",
    },
    post: {
      "Content-Type": "application/json",
    },
  },
});

axiosApi.interceptors.request.use(function (config) {
  const token = getTokenFromLocalStorage();
  config.headers = config.headers || {};

  if (token && !String(config?.url || "").includes("/token/refresh")) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }

  return config;
});

const isLoginRequest = (requestConfig) =>
  String(requestConfig?.url || "").includes("/client/login");

const isRefreshRequest = (requestConfig) =>
  String(requestConfig?.url || "").includes("/token/refresh");

const clearRefreshTimeout = () => {
  if (scheduledRefreshId) {
    clearTimeout(scheduledRefreshId);
    scheduledRefreshId = null;
  }
};

const clearStoredAuthSession = () => {
  deleteTokenFromLocalStorage();
  localStorage.removeItem("user");
};

const redirectToLogin = () => {
  if (typeof window === "undefined") return;

  const loginPath = "/login";
  if (window.location.pathname !== loginPath) {
    window.history.pushState({}, "", loginPath);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const hardLogout = () => {
  clearRefreshTimeout();
  clearStoredAuthSession();
  redirectToLogin();
};

const scheduleTokenRefresh = (expiresInSeconds) => {
  if (typeof window === "undefined") return;

  const expiresIn = Number(expiresInSeconds);
  if (Number.isNaN(expiresIn) || expiresIn <= 0) return;

  clearRefreshTimeout();
  const configuredDelay = Math.max(
    CONFIGURED_REFRESH_DELAY_MS,
    MIN_REFRESH_DELAY_MS,
  );
  const expiryBasedDelay = Math.max(
    expiresIn * 1000 - REFRESH_EARLY_MS,
    MIN_REFRESH_DELAY_MS,
  );
  const refreshDelay = Math.min(configuredDelay, expiryBasedDelay);

  scheduledRefreshId = window.setTimeout(() => {
    refreshAccessToken().catch(() => {
      hardLogout();
    });
  }, refreshDelay);
};

export const armRefreshTimerFromLogin = (expiresInSeconds) => {
  scheduleTokenRefresh(expiresInSeconds);
};

export const postRefreshToken = (refreshToken) => {
  return axios.post(
    `${API_URL}/token/refresh`,
    { refresh_token: refreshToken },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
};

export const refreshAccessToken = async () => {
  const refreshToken = getRefreshTokenFromLocalStorage();
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  if (refreshRequestPromise) {
    return refreshRequestPromise;
  }

  refreshRequestPromise = postRefreshToken(refreshToken)
    .then((response) => {
      storeAuthSession(response.data);
      scheduleTokenRefresh(response?.data?.expires_in);
      return response?.data?._token;
    })
    .finally(() => {
      refreshRequestPromise = null;
    });

  return refreshRequestPromise;
};

export const initializeAuthSession = () => {
  const token = hydrateSessionTokenFromPersistentStorage();
  const refreshToken = getRefreshTokenFromLocalStorage();
  const expiresAt = getTokenExpiryFromLocalStorage();

  if (!token || !refreshToken) return;

  const remainingMs = Number(expiresAt) - Date.now();

  if (!Number.isFinite(remainingMs) || remainingMs <= REFRESH_EARLY_MS) {
    refreshAccessToken().catch(() => {
      hardLogout();
    });
    return;
  }

  scheduleTokenRefresh(Math.floor(remainingMs / 1000));
};

axiosApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error?.config || {};

    if (isLoginRequest(originalRequest)) {
      return Promise.reject(error);
    }
    // A 401 from the delete-account endpoint means "wrong password", not an
    // expired session — let the caller show its inline error instead of
    // refreshing/logging out (parity with mobile behaviour).
    if (String(originalRequest?.url || "").includes("/client/account/delete-request")) {
      return Promise.reject(error);
    }
    if (error?.response?.status === 401) {
      if (isRefreshRequest(originalRequest) || originalRequest?._retry) {
        hardLogout();
        return Promise.reject(error);
      }

      if (!getRefreshTokenFromLocalStorage()) {
        hardLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosApi(originalRequest);
      } catch (refreshError) {
        hardLogout();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

// -----------------------------------------------AUTH-------------------------------------------------
export const postLogin = (data) => {
  return axiosApi.post(`/client/login`, data);
};

export const logoutApiCall = (data) => {
  return axiosApi.post(`/client/logout`, data);
};

export const registerApiCall = (data) => {
  return axiosApi.post(`/client/register`, data);
};

export const updateUserApiCall = (data) => {
  return axiosApi.post(
    `/client/${
      JSON.parse(localStorage.getItem("user"))?.user?.id
    }/update-profile`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

// -----------------------------------------------PROMOTIONS-------------------------------------------------
export const getAllPromotionsCall = () => {
  return axiosApi.get(`/client/promotions`);
};

// -----------------------------------------------STATIC PAGES-------------------------------------------------
export const getAllStaticPagesCall = async () => {
  return await axiosApi.get(`/client/static-pages`);
};

// -----------------------------------------------JACKPOTS-------------------------------------------------
export const getAllJackpotsCall = () => {
  return axiosApi.get(`/client/jackpots`);
};

export const getAllResultJackpotsCall = () => {
  return axiosApi.get(`/client/jackpots-result`);
};

export const getJackpotByIdCall = (id, perPage, marketId) => {
  var param = `?market_id=${marketId}`;

  if (perPage != 0) {
    param += `&page=${perPage + 1}`;
  }

  return axiosApi.get(`/client/jackpots/${id}${param}`);
};

export const getJackpotResultByIdCall = (id, perPage) => {
  var param = ``;

  if (perPage != 0) {
    param += `&page=${perPage + 1}`;
  }

  return axiosApi.get(`/client/view-jackpot-result/${id}${param}`);
};

// -----------------------------------------------LEAGUES-------------------------------------------------
export const getAllLeaguesCall = () => {
  return axiosApi.get(`/v4/leagues?perPage=1000`);
};

// -----------------------------------------------LOCATIONS-------------------------------------------------
export const getAllLocationsCall = () => {
  return axiosApi.get(`/v4/locations`);
};

// -----------------------------------------------SPORTS-------------------------------------------------
export const getAllSportsCall = () => {
  return axiosApi.get(`/v4/sports`);
};

// -----------------------------------------------FIXTURES-------------------------------------------------
const _fixturesCache = {};
const FIXTURES_TTL_MS = 10000;

export const getFixturesBySportIdCall = (
  url,
  matchTime = "all",
  marketChange = "1",
  perPage = "",
  queryChar = "?",
  day = "today",
  leagueIds = "",
  startDate,
  endDate,
  oneUp = false,
  twoUp = false,
) => {
  // Signature mirrors RN repository.ts getFixturesBySportIdCall
  let fullUrl = `${url}${queryChar}match_time=${matchTime}&market_id=${marketChange}&timezone_offset=${new Date().getTimezoneOffset()}&perPage=${perPage}&day=${day}&leagueIds=${leagueIds}`;
  if (startDate) fullUrl += `&start_date=${startDate}`;
  if (endDate) fullUrl += `&end_date=${endDate}`;
  if (oneUp === true) fullUrl += `&one_up=true`;
  if (twoUp === true) fullUrl += `&two_up=true`;

  const cached = _fixturesCache[fullUrl];
  const now = Date.now();
  if (cached && now - cached.ts < FIXTURES_TTL_MS) {
    return cached.promise;
  }
  const promise = axiosApi.get(fullUrl).catch((err) => {
    delete _fixturesCache[fullUrl];
    throw err;
  });
  _fixturesCache[fullUrl] = { promise, ts: now };
  return promise;
};

export const getFixturesByLeagueIdCall = (
  id,
  matchTime = "all",
  marketChange = "1",
  leagueIds = "",
) => {
  return axiosApi.get(
    `/v4/leagues/${id}/fixtures?match_time=${matchTime}&market_id=${marketChange}&timezone_offset=${new Date().getTimezoneOffset()}&perPage=20&leagueIds=${leagueIds}`,
  );
};
export const getFixtureResultCall = (sportID, matchTime, day) => {
  return axiosApi.get(
    `/v4/fixtures/result?sport_id=${sportID}&match_time=${matchTime}&day=${day}`,
  );
};

export const getTopLeaguesCall = (
  matchTime,
  marketChange,
  perPage,
  leagueIds,
  is1Up,
  is2Up,
) => {
  const leagueIdsParam = Array.isArray(leagueIds)
    ? leagueIds.join(",")
    : leagueIds;
  const url = `/v4/leagues/TopLeagues/fixtures?match_time=${matchTime}&market_id=${marketChange}&timezone_offset=${new Date().getTimezoneOffset()}&perPage=${perPage}&leagueIds=${leagueIdsParam}`;
  return axiosApi.get(url);
};

export const getFixtureByIdCall = (id) => {
  return axiosApi.get(`/v4/fixtures/${id}/show`);
};

export const getLiveFixtureByIdCall = (id) => {
  return axiosApi.get(`/v4/live/fixtures/${id}/show`);
};

export const getFixturesByDate = (
  sportID,
  matchChange,
  perPage,
  selectedDate = "today",
  startDate,
  endDate,
  oneUp = false,
  twoUp = false,
) => {
  let url = `/v4/sports/${sportID}/fixtures?market_id=${matchChange}&timezone_offset=-60&perPage=${perPage}&one_up=${oneUp}&two_up=${twoUp}`;

  if (selectedDate === "today" || selectedDate === "tomorrow") {
    url += `&day=${selectedDate}`;
  } else if (selectedDate === "custom") {
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
  }

  return axiosApi.get(url);
};

// -----------------------------------------------LIVE FIXTURES-------------------------------------------------
const _liveFixturesCache = {};
const LIVE_FIXTURES_TTL_MS = 5000;

export const getLiveFixturesBySportIdCall = (
  url = "",
  matchTime = "all",
  marketChange = "1",
  perPage = "20",
) => {
  const fullUrl = `${url}?match_time=${matchTime}&market_id=${marketChange}&perPage=${perPage}`;
  const cached = _liveFixturesCache[fullUrl];
  const now = Date.now();
  if (cached && now - cached.ts < LIVE_FIXTURES_TTL_MS) {
    return cached.promise;
  }
  const promise = axiosApi.get(fullUrl).catch((err) => {
    delete _liveFixturesCache[fullUrl];
    throw err;
  });
  _liveFixturesCache[fullUrl] = { promise, ts: now };
  return promise;
};

export const getLiveFixturesByLeagueIdCall = (
  id,
  matchTime = "all",
  marketChange = "1",
) => {
  return axiosApi.get(
    `/v4/live/leagues/${id}/fixtures?match_time=${matchTime}&market_id=${marketChange}`,
  );
};

// -----------------------------------------------LIVE SPORTS-------------------------------------------------
let _liveInfoPromise = null;
let _liveInfoTimestamp = 0;
const LIVE_INFO_TTL_MS = 10000;

export const getLiveSportsCall = () => {
  const now = Date.now();
  if (_liveInfoPromise && now - _liveInfoTimestamp < LIVE_INFO_TTL_MS) {
    return _liveInfoPromise;
  }
  _liveInfoTimestamp = now;
  _liveInfoPromise = axiosApi.get(`/v4/live/info`).catch((err) => {
    _liveInfoPromise = null;
    throw err;
  });
  return _liveInfoPromise;
};

export const getLiveSportCall = (id) => {
  return axiosApi.get(`/v4/live/info/${id}`);
};

const _leaguesBySportCache = {};   // populated once the response arrives
const _leaguesBySportPending = {}; // in-flight promise, so concurrent callers share one XHR

export const getAllLeaguesBySport = (id, sc, ec) => {
  // Already have data → call success callback immediately
  if (_leaguesBySportCache[id]) {
    sc(_leaguesBySportCache[id]);
    return Promise.resolve();
  }
  // Request already in flight → chain onto it instead of firing a new one
  if (_leaguesBySportPending[id]) {
    return _leaguesBySportPending[id]
      .then(() => { sc(_leaguesBySportCache[id] ?? []); })
      .catch(() => { if (ec) ec(); });
  }
  // First caller → fire the XHR and store the promise
  _leaguesBySportPending[id] = axiosApi
    .get(`/v4/sports/${id}/leagues?perPage=5000`)
    .then((res) => {
      _leaguesBySportCache[id] = res?.data?.data ?? [];
      sc(_leaguesBySportCache[id]);
    })
    .catch((e) => {
      delete _leaguesBySportPending[id]; // allow retry on next call
      if (ec) ec(e);
    });
  return _leaguesBySportPending[id];
};

let _regulatoryPromise = null;

export const getRegulatoryAndParameters = (sb) => {
  if (!_regulatoryPromise) {
    _regulatoryPromise = axiosApi.get(`/v2/regulatory-betting-limits`).catch((err) => {
      _regulatoryPromise = null;
      throw err;
    });
  }
  return _regulatoryPromise.then((res) => {
    sb(res.data);
  });
};
export const getCashBalanceLogs = (sb) => {
  return axiosApi
    .get(`/client/cash-balance-logs`)
    .then((res) => sb(res?.data))
    .catch(() => sb({ status: false }));
};

// -----------------------------------------------SEARCH FIXTURES-------------------------------------------------
export const searchFixture = (q = "", active_tab, marketChange, matchTime) => {
  return axiosApi.get(
    `/v4/search?q=${q}&active_tab=${active_tab}&match_time=${matchTime}&market_id=${marketChange}&timezone_offset=${new Date().getTimezoneOffset()}`,
  );
};

// -----------------------------------------------GET USER DETAILS-------------------------------------------------
export const getUserDetails = () => {
  return axiosApi.get(`/client/get-user-details`);
};
export const deleteAccountApiCall = (data) =>
  axiosApi.post(`/client/account/delete-request`, data);
export const getKycStatus = () => {
  return axiosApi.get(`/client/kyc/latest`);
};
// -----------------------------------------------Place Bet-------------------------------------------------
export const placeBetCall = (data, sc, ec) => {
  // const cancelTokenSource = axios.CancelToken.source();
  return axiosApi
    .post(`/client/place-bet`, data)
    .then((res) => {
      sc(res);
      // cancelTokenSource.cancel();
    })
    .catch((e) => {
      ec(e?.response?.data?.message ?? e?.response?.data?.error, e?.response?.data);
      // cancelTokenSource.cancel();
    });
};

export const placeSingleBetCall = async (data, sc, ec) => {
  try {
    const res = await axiosApi.post(`/client/place-single-bet`, data);
    sc(res);
  } catch (e) {
    ec(e?.response?.data?.message ?? e?.response?.data?.error, e?.response?.data);
  }
};
// -----------------------------------------------Book Bet-------------------------------------------------
export const bookBetCall = (data, sc, ec) => {
  return axiosApi
    .post(`/client/book-bet`, data)
    .then((res) => {
      sc(res);
      // cancelTokenSource.cancel();
    })
    .catch((e) => {
      console.log(e?.response?.data?.message);
      ec(e?.response?.data?.message ?? e?.response?.data?.error, e?.response?.data);
      // cancelTokenSource.cancel();
    });
};

// -----------------------------------------------Forgot password-------------------------------------------------
export const forgotPasswordCall = (data, sc, ec) => {
  return axiosApi
    .post(`/client/forgot-password`, data)
    .then((res) => {
      sc(res);
    })
    .catch((error) => {
      ec(error);
    });
};

// -----------------------------------------------Change Pin-------------------------------------------------
export const changePinCall = (data, sc, ec) => {
  return axiosApi
    .post(`/client/change-pin`, data)
    .then((res) => {
      sc(res);
    })
    .catch((error) => {
      ec(error);
    });
};

// -----------------------------------------------BANNERS-------------------------------------------------
export const getAllBannersCall = () => {
  return axiosApi.get(`/client/banners`);
};

let _blockedGamesPromise = null;

export const BlockedGames = () => {
  if (!_blockedGamesPromise) {
    _blockedGamesPromise = axiosApi.get(`/v4/blockedGames`).catch((err) => {
      _blockedGamesPromise = null;
      throw err;
    });
  }
  return _blockedGamesPromise;
};

// -----------------------------------------------Games-------------------------------------------------

export const getGamesCall = (game, sb) => {
  if (game == "crashx") {
    game = "crashfootball";
  }
  const isLoggedIn = sessionStorage.getItem("token") ? true : false;
  const userId = JSON.parse(localStorage.getItem("user"))?.user?.id;

  return axiosApi
    .get(`/turbo-games/${game}?isloggedin=${isLoggedIn}&userId=${userId}`)
    .then((res) => {
      sb(res?.data);
    });
};
// -----------------------------------------------Games-------------------------------------------------

export const getAviatorGameCall = (game, sb) => {
  const isLoggedIn = sessionStorage.getItem("token") ? true : false;
  const userId = JSON.parse(localStorage.getItem("user"))?.user?.id;

  return axiosApi.get(`v4/spribe/get-game-url/${game}`).then((res) => {
    sb(res?.data);
  });
};
// -----------------------------------------------Casino-------------------------------------------------
export const getAllCasinoGames = (sc, ec) => {
  return axiosApi
    .get(`/v4/casino-games`)
    .then((res) => {
      sc(res.data.data);
    })
    .catch((e) => {
      ec();
    });
};

export const getBoomingGames = (sc, ec) => {
  return axiosApi
    .get(`/booming/games`)
    .then((res) => {
      sc(res.data);
    })
    .catch((e) => {
      ec(e?.response?.data?.message ?? e?.response?.data?.error);
    });
};

export const getGameUrl = (id, sc, ec) => {
  return axiosApi
    .get(`/pragmatic-play/get-game-url/${id}`)
    .then((res) => {
      sc(res.data);
    })
    .catch((e) => {
      ec(e?.response?.data?.message ?? e?.response?.data?.error);
    });
};

let _userInfoPromise = null;

export const getUserInfo = (sc, ec) => {
  if (!_userInfoPromise) {
    _userInfoPromise = axiosApi("/client/info")
      .then((res) => {
        localStorage.setItem("userInfo", JSON.stringify(res?.data));
        return res?.data; // expose resolved data so per-caller chains below receive it
      })
      .catch((err) => {
        _userInfoPromise = null; // clear so the next caller can retry
        throw err;               // re-throw so each caller's ec() still fires
      });
  }
  // Chain per-caller callbacks onto the shared promise without mutating it
  return _userInfoPromise
    .then((data) => { if (sc) sc(data); })
    .catch((e) => { if (ec) ec(e?.response?.data?.message ?? e?.response?.data?.error); });
};

export const getLiveSports = (sc, ec) => {
  return axiosApi("/v4/live/sports")
    .then((res) => {
      sc(res.data.data.data);
    })
    .catch((e) => {
      ec(e?.response?.data?.message ?? e?.response?.data?.error);
    });
};

export const getMarketsBySportId = (id, sc, ec) => {
  return axiosApi(`/v4/sports/${id}/markets`)
    .then((res) => {
      sc(res.data.data);
    })
    .catch((e) => {
      ec(e?.response?.data?.message ?? e?.response?.data?.error);
    });
};

// TTL cache so concurrent callers on the same page (BetSlipWidget + MyBets desktop + MyBets mobile)
// share one XHR per unique category/type/sort combination instead of each firing independently.
const _myBetsCache = {};
const MY_BETS_TTL_MS = 15000;

export const getMyBetsData = (category, type, sc, ec, sortType = "latest") => {
  const url = `/client/my-bets/${category.toLowerCase()}/${type.toLowerCase()}/${sortType.toLowerCase()}`;
  const cached = _myBetsCache[url];
  const now = Date.now();
  if (cached && now - cached.ts < MY_BETS_TTL_MS) {
    return cached.promise
      .then((data) => { sc(data); })
      .catch((e) => { ec(e?.response?.data?.message ?? e?.response?.data?.error); });
  }
  const promise = axiosApi(url)
    .then((res) => res.data.data)
    .catch((err) => { delete _myBetsCache[url]; throw err; });
  _myBetsCache[url] = { promise, ts: now };
  return promise
    .then((data) => { sc(data); })
    .catch((e) => { ec(e?.response?.data?.message ?? e?.response?.data?.error); });
};

// -----------------------------------------------SEARCH casino games-------------------------------------------------
export const searchGames = (id, q = "") => {
  return axiosApi.get(`/v4/casino-games-search/${id}?search=${q}`);
};

// -----------------------------------------------Deposite Amount-------------------------------------------------
export const callDepositeAmount = (data) => {
  return axiosApi.post(`/client/deposite-amount`, data);
};
// -----------------------------------------------Deposite Amount-------------------------------------------------
export const callDepositeAmountThroghModal = (data) => {
  return axiosApi.post(`/client/deposite-amount-through-modal`, data);
};
// -----------------------------------------------Withdraw Amount-------------------------------------------------
export const callWithdrawAmount = (data) => {
  return axiosApi.post(`/client/withdraw-amount`, data);
};
// --------------------------------------------------------------Transaction History----------------------------------
export const getTransactionHistory = async (transaction_type, params = {}) => {
  return axiosApi.get(`/client/get-tranasaction/${transaction_type}`, {
    params,
  });
};
// -----------------------------------------------Affiliate (parity with mobile) -------------------------------------
export const postAffiliateJoin = (referralCode) =>
  axiosApi.post(`/client/affiliate/join`, { referral_code: referralCode });

export const getAffiliateSummary = () => axiosApi.get(`/client/affiliate/summary`);

export const postAffiliateWithdraw = (amount) =>
  axiosApi.post(`/client/affiliate/withdraw`, { amount });

export const getAffiliateTransactions = (page = 1, perPage = 20) =>
  axiosApi.get(`/client/affiliate/transactions`, { params: { page, per_page: perPage } });

export const getAffiliateReferrals = (status = "all", page = 1, perPage = 20) =>
  axiosApi.get(`/client/affiliate/referrals`, { params: { status, page, per_page: perPage } });

// -----------------------------------------------Affiliate Earning-------------------------------------------------

export const callAffiliateEarning = (id) => {
  return axiosApi.get(`/client/get-raf-earning/${id}`);
};
// -----------------------------------------------Header Menu-------------------------------------------------
export const HeaderMenu = () => {
  return axiosApi.get(`/v4/header-menu`);
};

//-----------------------------------------------Slotegrator games-------------------------------------------------
export const getSlotegratorGameCall = (game, sb) => {
  const isLoggedIn = sessionStorage.getItem("token") ? true : false;
  const userId = JSON.parse(localStorage.getItem("user"))?.user?.id;
  const requestData = {
    isloggedin: isLoggedIn,
    userId: userId,
    game: game,
  };
  if (isLoggedIn) {
    return axiosApi
      .post(`/slotegrator/get-game-url`, requestData)
      .then((res) => {
        sb(res?.data);
      });
  } else {
    return axiosApi
      .post(`/slotegrator/get-game-url-demo`, requestData)
      .then((res) => {
        sb(res?.data);
      });
  }
};
export const getSlotGameCall = (game, sb) => {
  const isLoggedIn = sessionStorage.getItem("token") ? true : false;
  const userId = JSON.parse(localStorage.getItem("user"))?.user?.id;
  const requestData = {
    isloggedin: isLoggedIn,
    userId: userId,
    game_id: game,
  };
  if (isLoggedIn) {
    return axiosApi.post(`/endorphina/launch`, requestData).then((res) => {
      sb(res?.data);
    });
  } else {
    return;
  }
};
export const getSlotegratoBoomingGameCall = (game, sb) => {
  const isLoggedIn = sessionStorage.getItem("token") ? true : false;
  const userId = JSON.parse(localStorage.getItem("user"))?.user?.id;
  const requestData = {
    isloggedin: isLoggedIn,
    userId: userId,
    game_id: game,
  };
  if (isLoggedIn) {
    return axiosApi.post(`/booming/get-game-url`, requestData).then((res) => {
      sb(res?.data);
    });
  } else {
    return axiosApi
      .post(`/booming/get-game-url-demo`, requestData)
      .then((res) => {
        sb(res?.data);
      });
  }
};
//-----------------------------------------------Booming games-------------------------------------------------
export const getBoomingGamesCall = (game, sb, ec) => {
  const isLoggedIn = sessionStorage.getItem("token") ? true : false;
  const userId = JSON.parse(localStorage.getItem("user"))?.user?.id;
  const requestData = {
    isloggedin: isLoggedIn,
    userId: userId,
    game_id: game,
  };
  return axiosApi
    .post(`/booming/get-game-url`, requestData)
    .then((res) => {
      sb(res?.data);
    })
    .catch((e) => {
      ec(e?.response?.data?.message ?? e?.response?.data?.error);
    });
};

//--------------------------------------------------------search slotegrator games--------------------------------
export const slotegratorSearchGames = (id, q = "") => {
  return axiosApi.get(
    `/slotegrator/slotegrator-games-search/${id}?search=${q}`,
  );
};

export const initiateFlutterPayment = ({ amount, email, txRef }) => {
  return axiosApi.post("/flutter-wave/payment-initiate", {
    amount,
    email,
    currency: "NGN",
    tx_ref: txRef,
  });
};

export const initiate54PayPayment = ({ amount, email, txRef }) => {
  return axiosApi.post("/pay54/initiate", {
    amount,
    email,
    reference: txRef,
  });
};
export const initiate54PayMobilePayment = ({
  amount,
  email,
  txRef,
  country,
  currency,
}) => {
  return axiosApi.post("/pay54/mobile-money", {
    amount,
    email,
    reference: txRef,
    currency,
    country,
  });
};
export const simulate54PayPayment = (amount, reference) => {
  return axiosApi.post(`/pay54/simulate`, {
    amount,
    reference,
  });
};
export const simulate54PayMobilePayment = (amount, reference) => {
  return axiosApi.post(`/pay54/simulate`, {
    amount,
    reference,
  });
};
export const initiatePagaPayment = (amount) => {
  return axiosApi.post("/paga/initialize", {
    amount,
  });
};

export const initiateDalaPayPayment = (amount) => {
  return axiosApi.post("/dalapay/deposit", {
    amount,
    provider_id: DALAPAY_PROVIDER_ID,
    country: DALAPAY_COUNTRY,
  });
};

// services/payment.ts or similar
export const verifyFlutterPayment = (transaction_id) => {
  return axiosApi.post(`/flutter-wave/transfer/verify`, {
    transaction_id: String(transaction_id),
  });
};

export const verifyPaystackPayment = (reference) => {
  return axiosApi.get(`/paystack/transfer/verify`, {
    params: {
      reference,
    },
  });
};
export const verify54PayPayment = (reference) => {
  return axiosApi.post(`/pay54/verify`, {
    reference,
  });
};

export const verifyPagaPayment = (reference) => {
  return axiosApi.post(`/paga/verify`, {
    reference,
  });
};

// order_id shape confirmed from wurabet-api-docs.postman_collection.json;
// response field names aren't documented there — verify against staging once live.
export const verifyDalaPayPayment = (order_id) => {
  return axiosApi.post(`/dalapay/transfer/verify`, {
    order_id,
  });
};

export const getBanks = () => {
  return axiosApi.get("/paystack/banks");
};

export const getPagaBanks = () => {
  return axiosApi.get("/paga/banks");
};

export const getDalaPayBanks = () => {
  return axiosApi.get("/dalapay/banks", {
    params: { provider_id: DALAPAY_PROVIDER_ID },
  });
};

export const verifyBankAccountName = (accountNumber, bankCode) => {
  return axiosApi.post("/paystack/verifyAccount", {
    account_number: accountNumber,
    bank_code: bankCode,
  });
};

export const verifyPagaBankAccountName = (accountNumber, bankCode) => {
  return axiosApi.post("/paga/verifyAccount", {
    account_number: accountNumber,
    bank_uuid: bankCode,
  });
};

export const paystackWithdrawal = (data) => {
  return axiosApi.post("/paystack/payout", data);
};

export const pagaWithdrawal = (data) => {
  return axiosApi.post("/paga/payout", data);
};

export const dalaPayWithdrawal = (data) => {
  return axiosApi.post("/dalapay/payout", data);
};

export const initiateEnd2EndPay = (data) => {
  return axiosApi.post("/e2e/initialize", data);
};

export const getPopularSlotegrator = () => {
  return axiosApi.get("/slotegrator/popular-games");
};

export const getCheckoutSession = (ticketId) => {
  return axiosApi.post("/client/my-sports-bets-cashout-information", ticketId);
};

export const confirmCashout = (ticketId) => {
  return axiosApi.post("/client/my-sports-bet-cashout", ticketId);
};

export const searchGame = (providerId, q) => {
  return axiosApi.get(
    `/slotegrator/slotegrator-games-search/${providerId}?search=${q}`,
  );
};

export const getSavedPaymentMethods = () => {
  return axiosApi.get("/user-cards");
};

export const deleteSavedPaymentMethod = (id) => {
  return axiosApi.delete(`/user-cards/${id}`);
};

export const depositWithSavedCard = (data) => {
  return axiosApi.post("/user-cards/charge", data);
};

export const getCurrentWithdrawalAccount = () => {
  return axiosApi.get("/paystack/account/current");
};

export const requestOtpForWithdrawalAccountChange = () => {
  return axiosApi.post("/paystack/account/change-request");
};

export const requestOtpForPagaWithdrawalAccountChange = () => {
  return axiosApi.post("/paga/account/change-request");
};

export const verifyRequestedOtpForWithdrawalAccountChange = (otp) => {
  return axiosApi.post("/paystack/account/change-verify", { otp_code: otp });
};

export const verifyRequestOtpForPagaWithdrawalAccountChange = (otp) => {
  return axiosApi.post("/paga/account/change-verify", { otp_code: otp });
};

export const confirmChangeWithdrawalAccount = (data) => {
  return axiosApi.post("/paystack/account/change-confirm", data);
};

export const confirmChangePagaWithdrawalAccount = (data) => {
  return axiosApi.post("/paga/account/change-confirm", data);
};

export const checkIfUserHasWithdrawalPin = () => {
  return axiosApi.get("/client/check-pin-is-set");
};

export const createUserWithdrawalPin = (data) => {
  return axiosApi.post("/client/set-pin", data);
};

export const verifyUserWithdrawalPin = (data) => {
  return axiosApi.post("/client/verify-pin", data);
};

export const requestOtpForPinResetWithdrawalPin = (data) => {
  return axiosApi.post("/client/send-forgot-user-pin-otp", data);
};

export const resetWithdrawalPin = (data) => {
  return axiosApi.post("/client/verify-forgot-user-pin-otp", data);
};

// -----------------------------------------------Slotegrator static lists (singleton caches)-------------------------------------------------
// game-type and game-provider lists never change during a session; cache them
// so concurrent mounts (AppLayout sidebar + page) share one XHR each.
let _gameTypesPromise = null;
let _gameProvidersPromise = null;

export const getSlotegratorGameTypes = () => {
  if (!_gameTypesPromise) {
    _gameTypesPromise = axiosApi.get("slotegrator/game-type").catch((err) => {
      _gameTypesPromise = null;
      throw err;
    });
  }
  return _gameTypesPromise;
};

export const getSlotegratorProviders = () => {
  if (!_gameProvidersPromise) {
    _gameProvidersPromise = axiosApi.get("slotegrator/game-provider").catch((err) => {
      _gameProvidersPromise = null;
      throw err;
    });
  }
  return _gameProvidersPromise;
};

// -----------------------------------------------Slotegrator AllGames (deduplicated)-------------------------------------------------
let _allSlotegratorGamesPromise = null;

export const getAllSlotegratorGames = (type) => {
  const url = type ? `slotegrator/AllGames?type=${type}` : "slotegrator/AllGames";
  if (!type) {
    if (!_allSlotegratorGamesPromise) {
      _allSlotegratorGamesPromise = axiosApi.get(url).catch((err) => {
        _allSlotegratorGamesPromise = null;
        throw err;
      });
    }
    return _allSlotegratorGamesPromise;
  }
  return axiosApi.get(url);
};
